import React, { createContext, useContext, useEffect, useState } from "react";
import {
  GRAPHING_TOOL_FILTERS_API,
  GRAPHING_TOOL_REPORT_API,
} from "../config/apiConfig";
import {
  extractAndSortFieldsFromTickers,
  getRandomCSSColorCode,
} from "../utils/utils";
import { securedFetch } from "../utils/api";

const GraphingToolContext = createContext();

export const GraphingToolProvider = ({ children }) => {
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [filtersError, setFiltersError] = useState(null);

  const [graphingToolFilters, setGraphingToolFilters] = useState(null);

  const [tickers, setTickers] = useState([]);
  const [selectedTickers, setSelectedTickers] = useState([]);

  const [fields, setFields] = useState([]);
  const [selectedField, setSelectedField] = useState(null);

  const [selectedPresetDateRange, setSelectedPresetDateRange] = useState(null);
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);

  const [graphingToolData, setGraphingToolData] = useState([]);
  const [graphingToolDataLoading, setGraphingToolDataLoading] = useState(false);
  const [graphingToolDataError, setGraphingToolDataError] = useState(null);

  useEffect(() => {
    setFiltersLoading(true);
    setFiltersError(null);
    securedFetch(GRAPHING_TOOL_FILTERS_API, {
      method: "GET",
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error("Failed to fetch Graphing Tool filters data");
        }
        return res.json();
      })
      .then((data) => {
        setGraphingToolFilters(data);
        const { tickers } = data || {};
        setTickers(tickers || []);
      })
      .catch((error) => {
        setFiltersError(error.message);
      })
      .finally(() => {
        setFiltersLoading(false);
      });
  }, []);

  useEffect(() => {
    const flatternedFields = extractAndSortFieldsFromTickers(tickers);
    setFields(flatternedFields);
  }, [tickers]);

  useEffect(() => {
    if (selectedField && selectedStartDate && selectedEndDate) {
      setGraphingToolDataLoading(true);
      setGraphingToolDataError(null);
      securedFetch(GRAPHING_TOOL_REPORT_API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          field: selectedField,
          startDateRange: selectedStartDate?.format("YYYY-MM-DD"),
          endDateRange: selectedEndDate?.format("YYYY-MM-DD"),
        }),
      })
        .then((res) => {
          if (!res.ok) {
            throw new Error("Failed to fetch Graphing Tool report data");
          }
          return res.json();
        })
        .then((data) => {
          // Process the data to add random colors to each ticker
          const tickersWithColorUpdated = data?.tickers?.map((tickerObj) => {
            return { ...tickerObj, color: getRandomCSSColorCode() };
          });
          setGraphingToolData({ ...data, tickers: tickersWithColorUpdated });
        })
        .catch((error) => {
          setGraphingToolDataError(error.message);
        })
        .finally(() => {
          setGraphingToolDataLoading(false);
        });
    }
  }, [selectedField, selectedStartDate, selectedEndDate]);

  return (
    <GraphingToolContext.Provider
      value={{
        filtersLoading,
        filtersError,

        graphingToolFilters,

        tickers,
        setTickers,
        selectedTickers,
        setSelectedTickers,

        fields,
        setFields,
        selectedField,
        setSelectedField,

        selectedPresetDateRange,
        setSelectedPresetDateRange,
        selectedStartDate,
        setSelectedStartDate,
        selectedEndDate,
        setSelectedEndDate,

        graphingToolData,
        setGraphingToolData,
        graphingToolDataLoading,
        graphingToolDataError,
      }}
    >
      {children}
    </GraphingToolContext.Provider>
  );
};

export const useGraphingToolContext = () => useContext(GraphingToolContext);
